import moment, { Moment } from 'moment-timezone';
import { toMoment } from './date';
import {
	TimeZoneFormat,
	TimeZoneMode,
} from './constants';

export type TimeZoneValue = { iso: Moment | null; tz: string | null };

// Helpers for converting between stored ISO strings and Moment + timezone for UI (useful in our transformData)
// toISOTime - Converts Moment to ISO (for DB/storage) - Before saving as ISOString
export const toISOTimezone = (
	dateTime: Moment | null | undefined,
	timeZoneFormat: TimeZoneFormat,
	timeZone?: string | null,
	mode: TimeZoneMode = TimeZoneMode.Instant,
): string | null => {
	if (!dateTime?.isValid?.()) {
		return null;
	}

	const base = dateTime.clone();

	if (timeZoneFormat === TimeZoneFormat.UTC) {
		return base.utc().toISOString(true);
	}

	if (timeZone) {
		return base.tz(timeZone, mode === TimeZoneMode.WallTime).toISOString(true);
	}

	return base.toISOString(true);
};

export const toISOTimezoneInstant = (
	dateTime: Moment | null | undefined,
	timeZoneFormat: TimeZoneFormat,
	timeZone?: string | null,
): Moment | null => {
	if (!dateTime?.isValid?.()) {
		return null;
	}

	const tz = timeZone || TimeZoneFormat.UTC;
	const pickedDate = toMoment(dateTime as Moment);

	const instant = timeZoneFormat === TimeZoneFormat.LocalTime ?
		pickedDate.clone().tz(tz, true).valueOf() :
		pickedDate.clone().tz(TimeZoneFormat.UTC, true).valueOf();

	const stored = moment.tz(instant, tz);

	return stored;
};

// fromISOTime - Converts ISO to Moment (for UI/display) - After reading
export const fromISOTimezone = (
	isoString: string | null | undefined,
	timeZoneFormat: TimeZoneFormat,
	timeZone?: string | null,
	mode: TimeZoneMode = TimeZoneMode.Instant,
): Moment | null => {
	if (!isoString) {
		return null;
	}

	// eslint-disable-next-line no-restricted-syntax
	const m = moment.parseZone(isoString);

	if (!m.isValid()) {
		return null;
	}

	if (timeZoneFormat === TimeZoneFormat.UTC) {
		return m.clone().utc();
	}

	if (timeZone) {
		if (mode === TimeZoneMode.Instant) {
			return m.clone().tz(timeZone);
		}

		if (mode === TimeZoneMode.WallTime) {
			const localWall = moment.tz(
				m.format('YYYY-MM-DDTHH:mm:ss'),
				'YYYY-MM-DDTHH:mm:ss',
				timeZone,
			);

			return localWall;
		}
	}

	return m;
};

export const formatOffset = (mins: number) => {
	const sign = mins >= 0 ? '+' : '-';
	const abs = Math.abs(mins);
	const hh = String(Math.floor(abs / 60)).padStart(2, '0');
	const mm = String(abs % 60).padStart(2, '0');

	return `LT (${TimeZoneFormat.UTC}${sign}${hh}:${mm})`;
};

export const timeZoneOffsetLabel = (
	iso: Moment | null,
	tz: string | null | undefined,
	timeZoneFormat: TimeZoneFormat,
): string => {
	if (timeZoneFormat === TimeZoneFormat.UTC) {
		return TimeZoneFormat.UTC;
	}

	if (!iso || !tz) {
		return '';
	}

	const off = moment.tz(
		iso.format('YYYY-MM-DDTHH:mm:ss.SSS'),
		'YYYY-MM-DDTHH:mm:ss.SSS',
		tz,
	).utcOffset();

	return formatOffset(off);
};
